// screens/Clark/researchGate.js — RUN OR. The signup gate on "Open in Research".
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 THE HOLE THIS CLOSES, MEASURED SIGNED OUT ON THE SERVED PAGE BEFORE ANY CHANGE.
//
// `Open in Research` is an `<a href>`. Its href is real and it works with JavaScript disabled,
// which is deliberate and correct (ClarkAnswerV2's own note explains why the control stopped being
// a button). What was missing is that nothing checked for a session before the browser followed it:
//
//   left-click,  1440 → navigated to /research/workspace?from=clark&answerId=…  NO gate, no modal
//   left-click,   390 → same
//   ⌘-click,     1440 → same URL, in a NEW TAB                                  NO gate
//
// RUN CK found the middle-click half of this and fixed the `click` path in the homepage widget;
// what nobody had was a gate on `/clark` itself. RUN CH3 then made `/clark` reachable for anonymous
// visitors, which turned a latent hole into a live one on the page strangers now land on.
//
// ⚠️ `auxclick` IS NOT `click`. A middle-click fires `auxclick`, never `click`, so a handler bound
// only to `onClick` cannot see it and cannot prevent it. A ⌘-click DOES fire `click` (with
// `metaKey` set), so one `preventDefault` covers it. Both are handled; both are needed.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 THE GATE LIVES AT THE DOOR, NOT AT THE CALLER — AND THAT IS THE POINT.
//
// The obvious fix is to make `thread/Turn.jsx`'s handler call `preventDefault`. That fixes the one
// caller that exists today and leaves the next one to rediscover this. There are THREE render
// sites of `data-testid="clark-action-research"` (RUN RM's report said two; `parts.jsx` is the
// third), and the components that own them are presentational — a caller who passes no handler at
// all still renders a live link. So the check belongs in the components that render the anchor,
// where it cannot be forgotten by a caller who does not know it exists.
//
// 🔴 A SIGNED-IN USER IS UNAFFECTED, BY CONSTRUCTION. This returns `false` for them before touching
// the event, so the anchor's default navigation runs exactly as it did: same href, same payload
// written by whatever handler the caller passes, same landing. clark→research is one of
// handoff.js's original two directions and a real feature for them.
//
// ⚠️ IT READS `window.Clerk`, NOT A HOOK — the same choice, for the same reason, as
// `screens/Clark/userIdentity.js`: "anything that reaches Clerk context throws and blanks the whole
// route" (D11). This module is imported by components that render inside the marketing island's
// bundle as well as the SPA, and that bundle has no ClerkProvider at all.

/**
 * Should this click be stopped and sent to the sign-up gate instead?
 *
 * Call from BOTH `onClick` and `onAuxClick` on any anchor that leaves Clark for Research.
 *
 * @param {MouseEvent} e  the click or auxclick event
 * @returns {boolean} true if the navigation was blocked (caller must not proceed), false to allow
 */
/**
 * Is there no session? Same read, same fail-closed direction, as the gate below — kept beside it so
 * the two can never disagree about who is signed in.
 * @returns {boolean} true when signed OUT (or when Clerk is unreadable).
 */
export function isSignedOut() {
  try { return !window.Clerk?.user; } catch { return true; }
}

export function blockResearchIfSignedOut(e) {
  let signedIn = false;
  try {
    signedIn = !!window.Clerk?.user;
  } catch {
    // 🔴 FAIL CLOSED. If we cannot tell whether there is a session, we do NOT hand out the
    // destination — an unreadable Clerk is the case where a stranger is most likely on the other
    // side of it. The cost of being wrong here is one signed-in user seeing a sign-up modal they
    // can dismiss; the cost of the other error is uplift given away past the gate.
    signedIn = false;
  }
  if (signedIn) return false;

  // 🔴 `preventDefault` ALONE IS NOT ENOUGH ON AN AUXCLICK PATH. Some browsers begin the new-tab
  // open on `mousedown`/`pointerdown` for the middle button; stopping propagation as well keeps
  // any ancestor handler from re-issuing it. Neither call throws on a synthetic event.
  try { e?.preventDefault?.(); } catch { /* not a real event */ }
  try { e?.stopPropagation?.(); } catch { /* not a real event */ }

  /* 🔴 RUN CK — THE BLOCKED CLICK NOW OPENS THE PRODUCT'S GATE MODAL (§4 TRIGGER A), NOT CLERK'S
     STOCK DIALOG. It is announced as a DOM EVENT rather than a prop, for exactly the reason this
     module exists: there are three render sites of this anchor and the components that own them
     are presentational, so a gate that has to be wired by a caller is a gate someone forgets.

     ⚠️ AND IT FALLS BACK, BECAUSE THIS FILE IS IMPORTED BY THE MARKETING ISLAND TOO. That bundle
     renders the answer components without Clark's screen, so nothing there listens. `Clark/index.jsx`
     sets `window.__ccClarkGate = 1` while it is mounted; without that flag we go to Clerk exactly as
     before. A visitor is never left with a click that did nothing. */
  let handled = false;
  try {
    if (window.__ccClarkGate) {
      window.dispatchEvent(new CustomEvent("cc:clark-gate", { detail: { source: "research" } }));
      handled = true;
    }
  } catch { /* no CustomEvent (very old browser) — fall through to Clerk */ }
  if (!handled) {
    // The auth island monkey-patches this to open the product's own navy modal rather than Clerk's
    // stock dialog. Calling through `window.Clerk` rather than importing src/auth/index.jsx avoids
    // re-running that island's import-time mount against a DOM node it already owns.
    try { window.Clerk?.openSignUp?.({}); } catch { /* Clerk not ready — click still blocked */ }
  }
  return true;
}

export default blockResearchIfSignedOut;
